import { Component, Renderer2 } from '@angular/core';
import { Router, RouterOutlet, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs';
import { BlockUI, BlockUIModule, NgBlockUI } from 'ng-block-ui';
import { HttpClient } from '@angular/common/http';
import { LogService } from './shared/services/log.service';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { ConfigService } from './shared/services/config.service';
import { OpenTelemetryService } from './shared/services/open-telemetry.service';
import { Capacitor } from '@capacitor/core';
import { App, URLOpenListenerEvent } from '@capacitor/app';
import { AuthService } from './shared/services/auth.service';
import { PushNotificationService } from './shared/services/push-notification.service';

@Component({
	selector: 'app-root',
	standalone: true,
	imports: [BlockUIModule, RouterOutlet],
	template: `
    <block-ui>
      <router-outlet></router-outlet>
    </block-ui>
  `,
	styles: [],
})
export class AppComponent {
	@BlockUI() blockUI!: NgBlockUI;

	constructor(
		private config: ConfigService,
		translateService: TranslateService,
		openTelemetryService: OpenTelemetryService,
		private authService: AuthService,
		private router: Router,
		private renderer: Renderer2,
		private http: HttpClient,
		private pushNotificationService: PushNotificationService,
	) {
		this.checkVersion();
		// Gestione classi platform sul body
		this.router.events.pipe(filter((event) => event instanceof NavigationEnd)).subscribe((event: any) => {
			const url = event.urlAfterRedirects;
			const isPublicSignatureMobile = url.includes('/web/public/firma-otp/dettaglio') && window.innerWidth <= 768;

			if (url.startsWith('/app') || url.includes('/app/') || isPublicSignatureMobile) {
				this.renderer.addClass(document.body, 'platform-app');
				this.renderer.removeClass(document.body, 'platform-web');
			} else if (url.startsWith('/web') || url.includes('/web/')) {
				this.renderer.addClass(document.body, 'platform-web');
				this.renderer.removeClass(document.body, 'platform-app');
			}
		});

		// Controllo piattaforma e reindirizzamento iniziale
		const isNative = Capacitor.isNativePlatform();
		const currentPath = window.location.pathname;

		if (currentPath === '/' || currentPath === '') {
			if (isNative) {
				router.navigate(['/app/public/login']);
			} else {
				router.navigate(['/web/public/login']);
			}
		}

		// Configurazione traduzioni
		translateService.setDefaultLang('it');
		translateService.use('it');

		// Configurazione Sentry
		if (config.configuration.sentryEnabled) {
			Sentry.init({
				dsn: config.configuration.sentryDsn,
				environment: config.configuration.environment,
				integrations: [],
				release: `RELEASE_${config.configuration.buildNumber}`,
			});
		}

		// Configurazione OpenTelemetry
		if (config.configuration.openTelemetryEnabled) {
			const openTelemetryOptions = {
				apiUrl: config.configuration.openTelemetryApiUrl,
				serviceName: config.configuration.openTelemetryServiceName,
				attributes: config.configuration.openTelemetryAttributes + config.configuration.environment,
				trace: true,
				log: true,
			};
			openTelemetryService.inizializza(openTelemetryOptions);
			const { utente } = JSON.parse(localStorage['user'] || '{}');
			openTelemetryService.attributiFissi.idUtente = utente ? String(utente.id) : '';
			openTelemetryService.attributiFissi.emailUtente = utente ? utente.user : '';
			openTelemetryService.attributiFissi.nomeUtente = utente ? `${utente.nome} ${utente.cognome}` : '';
			openTelemetryService.abilitaLoggingPagineVisitate();
		}

		// Gestione Auto-login all'avvio (Mobile)
		this.initializeApp();

		// Gestione deep linking per login Microsoft (Capacitor)
		if (isNative) {
			App.addListener('appUrlOpen', (event: URLOpenListenerEvent) => {
				const url = event.url;
				if (url.includes('auth/microsoft/callback')) {
					const slug = url.split('://')[1];
					if (slug) {
						this.router.navigateByUrl('/' + slug);
					}
				}
			});
		}
	}

	async initializeApp() {
		await this.authService.restoreSessionFromMobile();
		const hasRefreshToken = !!localStorage.getItem('refreshToken');

		// Le notifiche push verranno inizializzate dopo il login (es. nel PrivateComponent)
		// per evitare l'invio del token push prima di essere autenticati.

		if (hasRefreshToken) {
			this.authService.refreshToken().subscribe({
				next: () => {
					if (this.router.url.includes('/login')) {
						const isNative = Capacitor.isNativePlatform();
						this.router.navigate([isNative ? '/app/dashboard' : '/web/private']);
					}
				},
				error: () => {
					// Refresh fallito, AuthService si occupa già del logout locale
				},
			});
		}
	}

	private checkVersion() {
		if (!Capacitor.isNativePlatform()) {
			return;
		}

		this.config.checkAppVersion(this.http).subscribe(async (serverVersion: number) => {
			const info = await App.getInfo();
			const localVersion = Number(info.build);

			if (serverVersion > localVersion) {
				this.blockUI.start("E' disponibile una nuova versione. Installa l'aggiornamento per continuare.");
				return;
			}
		});
	}
}
